import {Component, ElementRef, HostListener, OnDestroy, OnInit, Optional, ViewChild, ViewEncapsulation} from '@angular/core';
import {animate, AUTO_STYLE, state, style, transition, trigger} from '@angular/animations';
import {MenuItems} from '../../shared/menu-items/menu-items';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../services/authentication.service';
import { ThemeService } from '../../shared/theme/theme.service';
import { DashboardToolbarService } from '../../shared/dashboard-toolbar.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.css'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('mobileMenuTop', [
            state('no-block, void', style({
                overflow: 'hidden',
                height: '0px',
            })),
            state('yes-block', style({
                height: AUTO_STYLE,
            })),
            transition('no-block <=> yes-block', [
                animate('400ms ease-in-out')
            ])
        ]),
        trigger('slideInOut', [
            state('in', style({
                transform: 'translate3d(0, 0, 0)'
            })),
            state('out', style({
                transform: 'translate3d(100%, 0, 0)'
            })),
            transition('in => out', animate('400ms ease-in-out')),
            transition('out => in', animate('400ms ease-in-out'))
        ]),
        trigger('slideOnOff', [
            state('on', style({
                transform: 'translate3d(0, 0, 0)'
            })),
            state('off', style({
                transform: 'translate3d(100%, 0, 0)'
            })),
            transition('on => off', animate('400ms ease-in-out')),
            transition('off => on', animate('400ms ease-in-out'))
        ]),
        trigger('fadeInOutTranslate', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('400ms ease-in-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ transform: 'translate(0)' }),
                animate('400ms ease-in-out', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})


export class AdminComponent implements OnInit, OnDestroy {
  sidebarOpts = { position: "right", barBackground: "#fff", barOpacity: "0.3", barWidth: "4", gridWidth: "0", alwaysVisible: true };
  chatOpts = { position: "right", barBackground: "#1b8bf9", barOpacity: "0.4", barWidth: "7", gridWidth: "0" };

  loingUserName : string ="";

  getUserInitials(): string {
    const fname = localStorage.getItem('fname') || '';
    const lname = localStorage.getItem('lname') || '';
    const i1 = fname && fname !== 'null' ? fname.trim()[0]?.toUpperCase() : '';
    const i2 = lname && lname !== 'null' ? lname.trim()[0]?.toUpperCase() : '';
    return (i1 + i2) || '?';
  }

  getUserRole(): string {
    const role = localStorage.getItem('role');
    return role && role !== 'null' ? role : '';
  }

  getUserDisplayName(): string {
    if (this.loingUserName && this.loingUserName.trim() !== '' && this.loingUserName !== 'null null') {
      return this.loingUserName;
    }
    return 'Signed in';
  }

  getPageTitle(): string {
    const segments = (this.router.url || '').split('?')[0].split('/').filter(segment => segment.length > 0);
    if (segments.length === 0 || segments[0] === 'dashboard') {
      return 'Dashboard';
    }

    const aliases: { [segment: string]: string } = {
      invoicepage: 'Invoices',
      vehicles: 'Customers & Vehicles',
      mechanicjobdetails: 'Job Details'
    };
    if (aliases[segments[0]]) {
      return aliases[segments[0]];
    }

    const menus = this.menuItems.getMenuByUserType() || [];
    for (const group of menus) {
      for (const item of group.main || []) {
        if (segments[0] !== item.state && segments[0] !== item.main_state) {
          continue;
        }
        const child = (item.children || []).find(entry => segments.includes(entry.state));
        return child ? child.name : item.name;
      }
    }

    return 'Workroo';
  }

  isDashboardRoute(): boolean {
    return (this.router.url || '').split('?')[0].startsWith('/dashboard');
  }

  onDashboardSearch(query: string): void {
    this.dashboardToolbarService?.setSearchQuery(query);
  }

  onGlobalSearchSubmit(query: string): void {
    this.dashboardToolbarService?.setSearchQuery(query);
    if (this.isDashboardRoute()) {
      return;
    }
    const term = (query || '').trim();
    this.router.navigate(['/joblist'], { queryParams: term ? { search: term } : {} });
  }

  requestNewDashboardJob(): void {
    const currentUrl = (this.router.url || '').split('?')[0] || '/dashboard';

    if (currentUrl === '/jobcomposer') {
      const existingBackTo = this.router.parseUrl(this.router.url).queryParams['backTo'];
      this.router.navigate(['/jobcomposer'], { queryParams: { action: 'New', backTo: existingBackTo || '/dashboard' } });
      return;
    }

    if (currentUrl === '/dashboard') {
      this.dashboardToolbarService?.requestNewJob();
      return;
    }

    this.router.navigate(['/jobcomposer'], { queryParams: { action: 'New', backTo: currentUrl } });
  }

  // Top bar search on every screen: the dashboard filters as you type,
  // other pages open the jobs list filtered by the query on Enter
  topBarSearchQuery = '';

  onTopBarSearchInput(value: string): void {
    this.topBarSearchQuery = value;
    if (this.isDashboardRoute()) {
      this.onDashboardSearch(value);
    }
  }

  onTopBarSearchSubmit(): void {
    const query = (this.topBarSearchQuery || '').trim();
    if (this.isDashboardRoute() || !query) {
      return;
    }
    this.router.navigate(['/joblist'], { queryParams: { search: query, page: 1 } });
  }

  // Figma shows "New Job" in the top bar on every screen. The composer's Back
  // button only knows the jobs list and schedule, so only those pass backTo.
  onTopBarNewJob(): void {
    if (this.isDashboardRoute()) {
      this.requestNewDashboardJob();
      return;
    }
    const path = (this.router.url || '').split('?')[0];
    const backTo = path.startsWith('/joblist') ? 'joblist' : path.startsWith('/schedule') ? '/schedule' : null;
    const queryParams: { action: string; backTo?: string } = { action: 'New' };
    if (backTo) {
      queryParams.backTo = backTo;
    }
    this.router.navigate(['/jobcomposer'], { queryParams });
  }

  isUserMenuOpen = false;

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  closeUserMenu(): void {
    this.isUserMenuOpen = false;
  }

  logout(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isUserMenuOpen = false;
    this.authenticationService.logout();
    this.router.navigate(['/authentication/login/page']);
  }



  navType: string; /* st1, st2(default), st3, st4 */
  themeLayout: string; /* vertical(default) */
  layoutType: string; /* dark, light */
  readonly darkThemeStorageKey = 'darkThemeEnabled';
  isDarkTheme: boolean = false;
  verticalPlacement: string; /* left(default), right */
  verticalLayout: string; /* wide(default), box */
  deviceType: string; /* desktop(default), tablet, mobile */
  verticalNavType: string; /* expanded(default), offcanvas */
  verticalEffect: string; /* shrink(default), push, overlay */
  vNavigationView: string; /* view1(default) */
  pcodedHeaderPosition: string; /* fixed(default), relative*/
  pcodedSidebarPosition: string; /* fixed(default), absolute*/
  headerTheme: string; /* theme1(default), theme2, theme3, theme4, theme5, theme6 */
  logoTheme: string; /* theme1(default), theme2, theme3, theme4, theme5, theme6 */

  innerHeight: string;
  windowWidth: number;

  toggleOn: boolean;

  isToggleClicked : boolean = false;

  headerFixedMargin: string;
  navBarTheme: string; /* theme1, themelight1(default)*/
  activeItemTheme: string; /* theme1, theme2, theme3, theme4(default), ..., theme11, theme12 */

  isCollapsedMobile: string;
  isCollapsedSideBar: string;

  chatToggle: string;
  chatToggleInverse: string;
  chatInnerToggle: string;
  chatInnerToggleInverse: string;

  menuTitleTheme: string; /* theme1, theme2, theme3, theme4, theme5(default), theme6 */
  itemBorder: boolean;
  itemBorderStyle: string; /* none(default), solid, dotted, dashed */
  subItemBorder: boolean;
  subItemIcon: string; /* style1, style2, style3, style4, style5, style6(default) */
  dropDownIcon: string; /* style1(default), style2, style3 */
  configOpenRightBar: string;
  isSidebarChecked: boolean;
  isHeaderChecked: boolean;



  @ViewChild('searchFriends', {static: false}) search_friends: ElementRef;
/*  @ViewChild('toggleButton', {static: false}) toggle_button: ElementRef;
  @ViewChild('sideMenu', {static: false}) side_menu: ElementRef;*/

  private themeSub: Subscription;
  private dashboardToolbarSub?: Subscription;
  dashboardSearchQuery = '';

  constructor(public menuItems: MenuItems, private router: Router, private authenticationService: AuthenticationService,
              private themeService: ThemeService, @Optional() private dashboardToolbarService?: DashboardToolbarService) {
    this.navType = 'st2';
    this.themeLayout = 'vertical';
    this.vNavigationView = 'view1';
    this.verticalPlacement = 'left';
    this.verticalLayout = 'wide';
    this.deviceType = 'desktop';
    this.verticalNavType = 'expanded';
    this.verticalEffect = 'shrink';
    this.pcodedHeaderPosition = 'fixed';
    this.pcodedSidebarPosition = 'fixed';
    this.headerTheme = 'theme1';
    this.logoTheme = 'theme1';

    this.toggleOn = true;

    this.headerFixedMargin = '80px';
    this.navBarTheme = 'themelight1';
    this.activeItemTheme = 'theme4';

    this.isCollapsedMobile = 'no-block';
    this.isCollapsedSideBar = 'no-block';

    this.chatToggle = 'out';
    this.chatToggleInverse = 'in';
    this.chatInnerToggle = 'off';
    this.chatInnerToggleInverse = 'on';

    this.menuTitleTheme = 'theme5';
    this.itemBorder = true;
    this.itemBorderStyle = 'none';
    this.subItemBorder = true;
    this.subItemIcon = 'style6';
    this.dropDownIcon = 'style1';
    this.isSidebarChecked = true;
    this.isHeaderChecked = true;

    const scrollHeight = window.screen.height - 150;
    this.innerHeight = scrollHeight + 'px';
    this.windowWidth = window.innerWidth;
    this.setMenuAttributes(this.windowWidth);
  }

  ngOnInit() {
    this.setBackgroundPattern('pattern2');
    const fname = localStorage.getItem('fname');
    const lname = localStorage.getItem('lname');
    this.loingUserName = [fname, lname].filter(part => part && part !== 'null').join(' ');

    const userType = localStorage.getItem('role');
    const storedDarkTheme = localStorage.getItem(this.darkThemeStorageKey);

    if (storedDarkTheme === 'true' || storedDarkTheme === 'false') {
      this.setLayoutType(storedDarkTheme === 'true' ? 'dark' : 'light', false);
    } else {
      this.setLayoutType(userType === 'Mechanic' ? 'dark' : 'light', false);
    }

    if (userType === 'Mechanic') {
      this.hideSideBarForMechanic(); // hideSideBar for Mechenic
    }

    this.themeSub = this.themeService.darkRequested$.subscribe(dark => this.setLayoutType(dark ? 'dark' : 'light'));
    this.dashboardToolbarSub = this.dashboardToolbarService?.searchQuery$.subscribe(query => {
      this.dashboardSearchQuery = query;
    });
  }

  ngOnDestroy() {
    this.themeSub?.unsubscribe();
    this.dashboardToolbarSub?.unsubscribe();
  }

  onResize(event) {
    this.innerHeight = event.target.innerHeight + 'px';
    /* menu responsive */
    this.windowWidth = event.target.innerWidth;
    let reSizeFlag = true;
    if (this.deviceType === 'tablet' && this.windowWidth >= 768 && this.windowWidth < 992) {
      reSizeFlag = false;
    } else if (this.deviceType === 'mobile' && this.windowWidth < 768) {
      reSizeFlag = false;
    }
    /* for check device */
    if (reSizeFlag) {
      this.setMenuAttributes(this.windowWidth);
    }
  }

  // Breakpoints match tokens.css / Bootstrap: phone < 768, tablet 768-991, desktop >= 992
  // (the top bar's sidebar toggle is .d-lg-none, i.e. visible below 992)
  setMenuAttributes(windowWidth) {
    if (windowWidth >= 768 && windowWidth < 992) {
      this.deviceType = 'tablet';
      this.verticalNavType = 'offcanvas';
      this.verticalEffect = 'push';
    } else if (windowWidth < 768) {
      this.deviceType = 'mobile';
      this.verticalNavType = 'offcanvas';
      this.verticalEffect = 'overlay';
    } else {
      this.deviceType = 'desktop';
      this.verticalNavType = 'expanded';
      this.verticalEffect = 'shrink';
    }
  }

  toggleOpened() {
    if (this.windowWidth < 992) {
      // Mobile: toggle sidebar open/close for ALL user types
      if (this.verticalNavType === 'offcanvas') {
        this.verticalNavType = 'expanded';
        this.toggleOn = false; // sidebar is open — don't auto-close on next body click
      } else {
        this.verticalNavType = 'offcanvas';
        this.toggleOn = true;
      }
    } else {
      // Desktop/tablet: Mechanic starts hidden and stays hidden unless explicitly toggled
      this.verticalNavType = this.verticalNavType === 'expanded' ? 'offcanvas' : 'expanded';
    }
  }

  onClickedOutside(e: Event) {
    // Close the slide-out sidebar when tapping outside it on phones and tablets
    if (this.windowWidth < 992 && this.verticalNavType !== 'offcanvas') {
      this.verticalNavType = 'offcanvas';
      this.toggleOn = true;
    }
  }

  //add by pubudu

  hideSideBarForMechanic(){
    this.toggleOn = true;
    this.verticalNavType = 'offcanvas';
  }


  onMobileMenu() {
    this.isCollapsedMobile = this.isCollapsedMobile === 'yes-block' ? 'no-block' : 'yes-block';
  }

  toggleChat() {
    this.chatToggle = this.chatToggle === 'out' ? 'in' : 'out';
    this.chatToggleInverse = this.chatToggleInverse === 'out' ? 'in' : 'out';
    this.chatInnerToggle = 'off';
    this.chatInnerToggleInverse = 'off';
  }

  toggleChatInner() {
    this.chatInnerToggle = this.chatInnerToggle === 'off' ? 'on' : 'off';
    this.chatInnerToggleInverse = this.chatInnerToggleInverse === 'off' ? 'on' : 'off';
  }

  searchFriendList(event) {
    const search = (this.search_friends.nativeElement.value).toLowerCase();
    let search_input: string;
    let search_parent: any;
    const friendList = document.querySelectorAll('.userlist-box .media-body .chat-header');
    Array.prototype.forEach.call(friendList, function(elements, index) {
      search_input = (elements.innerHTML).toLowerCase();
      search_parent = (elements.parentNode).parentNode;
      if (search_input.indexOf(search) !== -1) {
        search_parent.classList.add('show');
        search_parent.classList.remove('hide');
      } else {
        search_parent.classList.add('hide');
        search_parent.classList.remove('show');
      }
    });
  }

  toggleOpenedSidebar() {
    this.isCollapsedSideBar = this.isCollapsedSideBar === 'yes-block' ? 'no-block' : 'yes-block';
  }

  toggleRightbar() {
    this.configOpenRightBar = this.configOpenRightBar === 'open' ? '' : 'open';
  }

  setSidebarPosition() {
    this.isSidebarChecked = !this.isSidebarChecked;
    this.pcodedSidebarPosition = this.isSidebarChecked === true ? 'fixed' : 'absolute';
  }

  setHeaderPosition() {
    this.isHeaderChecked = !this.isHeaderChecked;
    this.pcodedHeaderPosition = this.isHeaderChecked === true ? 'fixed' : 'relative';
    this.headerFixedMargin = this.isHeaderChecked === true ? '80px' : '';
  }

  setBackgroundPattern(pattern) {
    document.querySelector('body').setAttribute('themebg-pattern', pattern);
  }

  onChangeTheme(e){
    this.setLayoutType(e ? 'dark' : 'light');
  }

  setLayoutType(type: string, persist: boolean = true) {
    this.layoutType = type;
    this.isDarkTheme = type === 'dark';

    if (persist) {
      localStorage.setItem(this.darkThemeStorageKey, String(this.isDarkTheme));
    }
    this.themeService.isDark$.next(this.isDarkTheme);

    if (type === 'dark') {
      this.headerTheme = 'theme6';
      this.navBarTheme = 'theme1';
      this.logoTheme = 'theme6';
      document.querySelector('body').classList.add('dark');
    } else {
      this.headerTheme = 'theme1';
      this.navBarTheme = 'themelight1';
      this.logoTheme = 'theme1';
      document.querySelector('body').classList.remove('dark');
    }
  }

  setNavBarTheme(theme: string) {
    if (theme === 'themelight1') {
      this.navBarTheme = 'themelight1';
    } else {
      this.navBarTheme = 'theme1';
    }
  }

  returnToHome(){

    if (localStorage.getItem('role') == "Mechanic"){
      this.router.navigate(['/mechanichome']);

    }
    else{
      this.router.navigate(['/']);
    }

  }


}
